import { FileOutlined } from '@ant-design/icons';
import {
  InfoProcura,
  getGetFrontRichiesteIdProcuratoreQueryOptions,
} from '@framework/api-client';
import {
  useAuthDownload,
  useHasDocumentsInlineViewer,
} from '@framework/common-web';
import { base64ToByteArray, getNetworkErrorMessage } from '@framework/ui';
import { Button, message } from 'antd';

export interface IAttorneyDownloadDelegationFileButtonProps {
  submissionId: string;
  fileId: string | null | undefined;
  label?: string;
}

/**
 * Pulsante che permette di scaricare il file di procura del procuratore,
 * se è stato caricato in fase di creazione della delega.
 * Disponibile sia per il front che per il back office.
 */
export function AttorneyDownloadDelegationFileButton(
  props: IAttorneyDownloadDelegationFileButtonProps,
) {
  const { fileId, submissionId, label } = props;

  const fileName = fileId?.split(/[\\/]/).pop();

  // Se il fileId è un PDF (lo riconosciamo dall'estensione), impostiamo il
  // mimetype a PDF: ci servirà per l'InlineViewer. In tutti gli altri casi,
  // lasciamo octet-stream in modo che sia il browser a decidere come gestirlo.
  const mimetype = fileId?.toLowerCase().endsWith('.pdf')
    ? 'application/pdf'
    : 'application/octet-stream';

  const hasInlineViewer = useHasDocumentsInlineViewer();

  const { download, isFetching } = useAuthDownload(
    getGetFrontRichiesteIdProcuratoreQueryOptions,
    [props.submissionId!, { includiFile: true }],
    {
      filename: fileName ?? `Procura_${props.submissionId}.pdf`,
      mimetype,
      parseData: (data?: InfoProcura) => {
        if (!data?.datiProcura?.fileBlob) {
          console.warn('Nessun file di procura disponibile', data);
          return null;
        }

        return base64ToByteArray(data?.datiProcura?.fileBlob ?? '');
      },
      textResponse: true,
      showInlineViewer: hasInlineViewer,
      onError: (e: any) => {
        message.error(
          getNetworkErrorMessage(
            e,
            'Si è verificato un problema durante il download del file',
          ),
        );
      },
    },
  );

  if (!fileId) return null;

  return (
    <Button size="middle" onClick={() => download()} loading={isFetching}>
      <FileOutlined aria-hidden /> {label ?? 'Documento di procura'}
    </Button>
  );
}
